import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';
import WhatsAppRegisterPanel from './WhatsAppRegisterPanel';
import PinLoginPanel from './PinLoginPanel';

// Protected passenger routes مشاويري/نقل عام/طرود redirect here with
// ?from=<path> so a successful login can return the passenger to the
// service they actually asked for. Validated against this allowlist rather
// than trusted as-is — never navigate to an arbitrary query-string value.
const ALLOWED_RETURN_PATHS = ['/rides', '/rides/new', '/transport', '/transport/new', '/parcels', '/parcels/new', '/profile'];

// Passenger login/registration. WhatsApp verification is the main/default
// path (phone + SMS-OTP is intentionally not offered here anymore — the
// backend still supports it at /api/auth/otp/*, but the UI no longer
// surfaces it); PIN login covers returning passengers. See
// backend/src/routes/authRoutes.js for the full set of auth mechanisms.
const PassengerLoginPage = () => {
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState('pin'); // 'whatsapp' | 'pin' — phone+PIN is the primary login form

  const rawFrom = searchParams.get('from') || '';
  const returnTo = ALLOWED_RETURN_PATHS.includes(rawFrom) ? rawFrom : null;

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen flex">
      {/* Left panel - decorative */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-bl from-[#1E3A8A] via-[#1E3A8A] to-[#1E40AF] relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 opacity-10">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="absolute rounded-full border border-white/30"
              style={{ width: `${(i + 2) * 80}px`, height: `${(i + 2) * 80}px`, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
          ))}
        </div>
        <div className="relative text-center text-white px-10">
          <img src="/logo.png" alt="سافر" className="w-52 h-52 object-cover rounded-[22%] mx-auto mb-6" />
          <p className="text-blue-100 text-lg mb-8">منصة حجز تذاكر البصات</p>
          <div className="grid grid-cols-3 gap-4 text-center">
            {[['🎫', 'حجز سريع'], ['🔒', 'دخول آمن بلا كلمة مرور'], ['📱', 'تذكرة فورية']].map(([icon, label]) => (
              <div key={label} className="bg-white/10 rounded-2xl p-4 backdrop-blur-sm border border-white/10">
                <div className="text-2xl mb-1">{icon}</div>
                <div className="text-xs text-white font-semibold">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#F5F9FF]">
        <div className="w-full max-w-md">

          {/* Logo for mobile */}
          <div className="lg:hidden text-center mb-6">
            <img src="/logo.png" alt="سافر" className="w-36 h-36 object-cover rounded-[22%] mx-auto mb-2" />
          </div>

          <div className="bg-white rounded-2xl shadow-[0_4px_24px_rgba(7,30,82,.10)] p-8 border border-[#E5E7EB]">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {mode === 'pin' ? 'تسجيل الدخول' : 'حساب جديد عبر واتساب'}
              </h2>
              <p className="text-gray-600 font-semibold text-sm mt-1">
                {mode === 'pin' ? 'أدخل رقم هاتفك والرقم السري للمتابعة' : 'أدخل اسمك ورقم هاتفك للتحقق عبر واتساب'}
              </p>
            </div>

            {returnTo && (
              <div className="mb-4 flex items-start gap-2 bg-blue-50 border border-blue-100 text-[#1E40AF] rounded-xl px-4 py-3 text-xs font-semibold">
                <span>ℹ️</span>
                <span>يمكنك حجز رحلات الباص كضيف. الخدمات الأخرى تتطلب تسجيل الدخول.</span>
              </div>
            )}

            {mode === 'whatsapp' && <WhatsAppRegisterPanel navigate={navigate} returnTo={returnTo} />}
            {mode === 'pin' && <PinLoginPanel navigate={navigate} returnTo={returnTo} />}

            {/* Switch between the two passenger auth mechanisms */}
            <div className="mt-4 text-center">
              {mode === 'whatsapp' ? (
                <button type="button" onClick={() => setMode('pin')}
                  className="block w-full text-xs text-[#1E40AF] hover:underline font-semibold">
                  لديك رمز PIN؟ سجّل الدخول به
                </button>
              ) : (
                <button type="button" onClick={() => setMode('whatsapp')}
                  className="block w-full text-xs text-gray-500 hover:underline font-semibold">
                  ليس لديك حساب؟ سجّل الآن
                </button>
              )}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-600 font-semibold">أو</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Guest mode CTA — always goes home, never the originally
                requested protected service (returnTo is intentionally
                ignored here). The sessionStorage flag is what lets
                PassengerHomeGate (App.js) let this guest session back onto
                "/" without bouncing straight back to this login screen. */}
            <button
              type="button"
              onClick={() => { sessionStorage.setItem('safer_guest_continue', '1'); navigate('/'); }}
              className="w-full flex items-center justify-center gap-2 border-2 border-[#1E40AF] hover:bg-[#1E40AF] text-[#1E40AF] hover:text-white font-bold py-3 rounded-xl transition-all text-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
              متابعة كضيف
            </button>
            <p className="text-center text-[11px] text-gray-700 font-semibold mt-2 leading-relaxed">
              يمكنك الحجز كزائر دون إنشاء حساب — الحساب يتيح لك متابعة حجوزاتك لاحقاً
            </p>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PassengerLoginPage;
